/**
 * Report Detail Page
 *
 * Complete detail view for a single report record with 3 tabs
 * Special feature: Run Report button to execute the report
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { persistedReportRecords } from '../../services/mockData/reports';
import { persistedReportConfigurations } from '../../services/mockData/reportConfigurations';
import { getRecordById, deleteRecord } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { LayoutSection } from '../../components/detail/LayoutSection';
import { usePageLayout, usePageLayoutTabs } from '../../hooks/useObjectManager';
import { ReportModal } from '../../components/modals/ReportModal';
import { FileText, BarChart3, History, Play, Download, Loader, Table as TableIcon } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useExecuteReport } from '../../hooks/useReportExecution';
import { ReportConfiguration, ReportResults, STANDARD_REPORTS } from '../../services/reportEngine';
import { useReportHistory, useReportExecutionTracker } from '../../hooks/useReportHistory';
import { ReportChart } from '../../components/reports/ReportChart';

interface ReportDetailPageProps {
  id: string;
  onBack: () => void;
}

export const ReportDetailPage: React.FC<ReportDetailPageProps> = ({ id, onBack }) => {
  const { success, info, error: showError } = useToast();
  const queryClient = useQueryClient();
  const { addExecution, getHistoryForReport } = useReportHistory();
  const tracker = useReportExecutionTracker();
  const tabs = usePageLayoutTabs('Report__c');
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 'overview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportResults, setReportResults] = useState<ReportResults | null>(null);
  const [chartType, setChartType] = useState<'table' | 'bar' | 'line' | 'pie' | 'summary'>('table');
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('chart');
  const executeMutation = useExecuteReport();

  const { data: report, isLoading } = useQuery({
    queryKey: ['report', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const record = await getRecordById('Report', id);
        if (!record) throw new Error('Report not found');
        return { ...record, name: record.name ?? record.label ?? (record as any).Name__c };
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allReports = persistedReportRecords.get();
      const found = allReports.find(r => r.id === id);
      if (!found) throw new Error('Report not found');
      return found;
    }
  });

  // Fetch page layout (uses Record Pages assignment system)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Report__c', report);

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    const displayName = report?.name ?? (report as any)?.Name__c ?? report?.label ?? id;
    if (!window.confirm(`Are you sure you want to delete "${displayName}"?`)) return;
    if (shouldUseGobodo()) {
      deleteRecord('Report', id)
        .then((ok) => {
          if (ok) {
            queryClient.invalidateQueries({ queryKey: ['reportListViewData'] });
            queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Report__c'] });
            success('Report deleted', `"${displayName}" has been deleted`);
            onBack();
          }
        })
        .catch(() => success('Error', 'Failed to delete report'));
      return;
    }
    const allReports = persistedReportRecords.get();
    persistedReportRecords.set(allReports.filter(r => r.id !== id));
    success('Report deleted', `"${displayName}" has been deleted`);
    onBack();
  };

  // Export results to CSV
  const exportToCSV = (results: ReportResults) => {
    if (!results || !report) return;

    // Create CSV header
    const header = results.columns.map(col => col.label).join(',');

    // Create CSV rows
    const rows = results.rows.map(row => {
      return results.columns.map(col => {
        const value = row[col.field];
        // Escape commas and quotes
        const formatted = String(value || '').replace(/"/g, '""');
        return `"${formatted}"`;
      }).join(',');
    });

    // Combine header and rows
    const csv = [header, ...rows].join('\n');

    // Create download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.name}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    success('Export successful', `Downloaded ${results.totalRecords} records`);
  };

  const handleRunReport = async () => {
    if (!report) return;

    // Start tracking execution time
    const startTime = tracker.start();

    // Priority order for loading configuration:
    // 1. Check for saved custom configuration
    // 2. Check standard reports
    // 3. Use default fallback
    const allConfigs = persistedReportConfigurations.get();
    const savedConfig = allConfigs.find(c => c.id === report.id);
    const standardReport = STANDARD_REPORTS.find(sr => sr.id === report.id);

    const config: ReportConfiguration = savedConfig || standardReport || {
      id: report.id as string,
      name: report.name as string,
      objectName: report.objectName as string,
      selectedFields: ['name', 'status', 'owner', 'createdDate'], // Default fallback fields
      chartType: 'table'
    };

    console.log('Using report configuration:', config);
    if (savedConfig) {
      console.log('✅ Using SAVED configuration for:', savedConfig.name);
    } else if (standardReport) {
      console.log('✅ Using STANDARD_REPORTS configuration for:', standardReport.name);
    } else {
      console.log('⚠️ Using default field configuration (no saved/standard report match)');
    }

    try {
      const results = await executeMutation.mutateAsync(config);

      // Calculate execution duration
      const duration = tracker.end(startTime);

      // Check if results are empty and show appropriate message
      if (results.totalRecords === 0) {
        info('No Results', `Report executed successfully but returned 0 records. Check filters and data availability.`);
      } else {
        success('Report executed', `Found ${results.totalRecords} record${results.totalRecords !== 1 ? 's' : ''} in ${duration}ms`);
      }

      setReportResults(results);
      setChartType(config.chartType || 'table');
      setActiveTab('results');

      // Add execution to history
      addExecution({
        reportId: report.id as string,
        reportName: report.name as string,
        timestamp: new Date().toISOString(),
        user: 'Current User', // TODO: Get from auth context
        recordCount: results.totalRecords,
        duration,
        objectName: config.objectName
      });

      // Update report with complete data in storage
      const now = new Date().toISOString();
      const allReports = persistedReportRecords.get();
      const updatedReports = allReports.map(r =>
        r.id === report.id
          ? {
              ...r,
              lastRunDate: now,
              lastModifiedDate: now,
              // Ensure all required fields exist
              type: r.type || 'Custom',
              objectName: r.objectName || config.objectName,
              format: r.format || 'Table',
              status: r.status || 'Active'
            }
          : r
      );
      persistedReportRecords.set(updatedReports);

      console.log('✅ Updated report in storage:', updatedReports.find(r => r.id === report.id));

      // Invalidate ALL report-related queries to force complete refresh
      queryClient.invalidateQueries({ queryKey: ['report', id] });
      queryClient.invalidateQueries({ queryKey: ['reportListViewData'] });
      queryClient.invalidateQueries({ queryKey: ['reportListViews'] });

      // Force immediate refetch
      queryClient.refetchQueries({ queryKey: ['report', id] });
      queryClient.refetchQueries({ queryKey: ['reportListViewData'] });

    } catch (error) {
      console.error('Report execution error:', error);
      showError('Error', 'Failed to execute report. Check console for details.');
    }
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Reports">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!report) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Reports">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Report not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  // Status badge colors
  const statusColors: Record<string, string> = {
    'Active': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    'Scheduled': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'Draft': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    'Archived': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
  };

  // Category badge colors
  const categoryColors: Record<string, string> = {
    'Sales': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    'Service': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    'Marketing': 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300',
    'Finance': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    'Operations': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
    'Custom': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300'
  };

  // Format date for subtitle
  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'Never run';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverview = ['overview', 'tab_001', 'details'].includes(activeTab);
  const isResults = ['results'].includes(activeTab);
  const isHistory = ['history'].includes(activeTab);

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Reports">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <DetailHeader
          title={report.name as string}
          subtitle={`Last Run: ${formatDate(report.lastRunDate as string | undefined)}`}
          recordId={report.id as string}
          status={report.status as string}
          statusColor={statusColors[report.status as string] || statusColors.Active}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Run Report Button */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <button
            onClick={handleRunReport}
            disabled={executeMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {executeMutation.isPending ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                Run Report
              </>
            )}
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {isOverview && (
              <div className="space-y-6">
                {loadingLayout ? (
                  // Loading state
                  <div className="space-y-6">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6"
                      >
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Render sections from page layout
                  pageLayout?.sections.map(section => (
                    <LayoutSection
                      key={section.id}
                      section={section}
                      objectApiName="Report"
                      record={report}
                    />
                  ))
                )}
              </div>
            )}

            {isResults && (
              <div className="space-y-4">
                {reportResults ? (
                  <>
                    {/* View Toggle & Actions */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setViewMode('chart')}
                          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                            viewMode === 'chart'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#334155]'
                          }`}
                        >
                          <BarChart3 className="w-4 h-4" />
                          Chart
                        </button>
                        <button
                          onClick={() => setViewMode('table')}
                          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                            viewMode === 'table'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#334155]'
                          }`}
                        >
                          <TableIcon className="w-4 h-4" />
                          Table
                        </button>
                      </div>

                      <button
                        onClick={() => exportToCSV(reportResults)}
                        className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        Export CSV
                      </button>
                    </div>

                    {/* Summary Statistics (if available) */}
                    {reportResults.summary && Object.keys(reportResults.summary).length > 0 && chartType !== 'summary' && (
                      <div className="grid grid-cols-4 gap-4">
                        {Object.entries(reportResults.summary).map(([key, stat]) => (
                          <div key={key} className="bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {stat.label}
                            </div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                              {typeof stat.value === 'number'
                                ? stat.value.toLocaleString()
                                : stat.value
                              }
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Chart or Table View */}
                    {viewMode === 'chart' ? (
                      // Show chart based on report type
                      <ReportChart
                        results={reportResults}
                        chartType={chartType}
                      />
                    ) : (
                      // Show table
                      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Report Results
                              </h3>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {reportResults.totalRecords} records • Executed {new Date(reportResults.executedAt).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-[#0f172a]">
                              <tr>
                                {reportResults.columns.map(col => (
                                  <th
                                    key={col.field}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                  >
                                    {col.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {reportResults.rows.map((row, index) => (
                                <tr
                                  key={index}
                                  className="hover:bg-gray-50 dark:hover:bg-[#1e293b]/50"
                                >
                                  {reportResults.columns.map(col => (
                                    <td
                                      key={col.field}
                                      className="px-6 py-4 text-sm text-gray-900 dark:text-white"
                                    >
                                      {row[col.field] ?? '—'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
                    <div className="text-center">
                      <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Run Report to See Results
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Click the "Run Report" button above to execute this report and view the results here.
                      </p>
                      <button
                        onClick={handleRunReport}
                        disabled={executeMutation.isPending}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {executeMutation.isPending ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            Running...
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4" />
                            Run Report
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isHistory && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Execution History
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      View past executions of this report
                    </p>
                  </div>
                </div>

                {(() => {
                  const history = getHistoryForReport(id);

                  if (history.length === 0) {
                    return (
                      <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
                        <div className="text-center">
                          <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            No Execution History
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Run this report to see execution history here.
                          </p>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-[#0f172a]">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Timestamp
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                User
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Object
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Records
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Duration
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {history.map((execution) => (
                              <tr
                                key={execution.id}
                                className="hover:bg-gray-50 dark:hover:bg-[#1e293b]/50"
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {new Date(execution.timestamp).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {execution.user}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {execution.objectName}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {execution.recordCount.toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                                  {execution.duration}ms
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {history.length > 0 && (
                        <div className="px-6 py-3 bg-gray-50 dark:bg-[#0f172a] border-t border-gray-200 dark:border-gray-700">
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Showing {history.length} execution{history.length !== 1 ? 's' : ''} • Last 100 executions kept
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        report={report}
      />
    </DetailPageLayout>
  );
};
